import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import FilterSelect from '../../common2/FilterSelect';
import ResetButton from './ResetButton';
import {
  claimTypeOptions,
  claimStatusOptions,
  claimStatusCountMap,
  claimShipmentTypesOptions,
  claimDorStatusOptions,
} from './filter-options';
import useCarriersOptions from './useCarriersOptions';
import useRecipientCountryOptions from './useRecipientCountryOptions';
import { useClaimsShopSettings } from '../../parcel-finder/ClaimsShopSettingsContext';
import WrappableRow from '../../common/WrappableRow/WrappableRow';

import './ClaimsFilters.scss';

function ClaimsFilters(props) {
  const {
    types = [],
    statuses = [],
    statusCounts = [],
    shipmentTypes = [],
    dorStatuses = [],
    carriers = [],
    claimsCounts = {},
    shopCarriers = [],
    recipientCountries = [],
    onApply,
    onClear,
    isLoading,
  } = props;

  // We have a separate useState for all the filters here to display what the user entered before they apply them
  const [selectedStatuses, setSelectedStatuses] = useState(statuses);
  const [selectedTypes, setSelectedTypes] = useState(types);
  const [selectedShipmentTypes, setSelectedShipmentTypes] = useState(shipmentTypes);
  const [selectedDorStatuses, setSelectedDorStatuses] = useState(dorStatuses);
  const [selectedCarriers, setSelectedCarriers] = useState(carriers);
  const [selectedRecipientCountries, setSelectedRecipientCountries] = useState(recipientCountries);

  const claimCarrierOptions = useCarriersOptions(shopCarriers, claimsCounts);
  const recipientCountryOptions = useRecipientCountryOptions(claimsCounts);

  const onApplyButtonClick = useCallback(() => {
    onApply({
      statuses: selectedStatuses,
      types: selectedTypes,
      shipmentTypes: selectedShipmentTypes,
      dorStatuses: selectedDorStatuses,
      carriers: selectedCarriers,
      recipientCountries: selectedRecipientCountries,
    });
  }, [
    selectedStatuses,
    selectedTypes,
    selectedShipmentTypes,
    selectedDorStatuses,
    selectedCarriers,
    selectedRecipientCountries,
    onApply,
  ]);

  const onClearButtonClick = useCallback(() => {
    if (
      statuses.length > 0 ||
      types.length > 0 ||
      shipmentTypes.length > 0 ||
      dorStatuses.length > 0 ||
      carriers.length > 0 ||
      recipientCountries.length > 0
    ) {
      onClear();
    }

    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedShipmentTypes([]);
    setSelectedDorStatuses([]);
    setSelectedCarriers([]);
    setSelectedRecipientCountries([]);
  }, [
    types,
    statuses,
    shipmentTypes,
    dorStatuses,
    carriers,
    recipientCountries,
    setSelectedTypes,
    setSelectedStatuses,
    setSelectedShipmentTypes,
    setSelectedDorStatuses,
    setSelectedCarriers,
    setSelectedRecipientCountries,
    onClear,
  ]);

  const onCloseStatusFilter = useCallback(() => {
    setSelectedStatuses(statuses);
  }, [statuses, setSelectedStatuses]);

  const onCloseTypeFilter = useCallback(() => {
    setSelectedTypes(types);
  }, [types, setSelectedTypes]);

  const onCloseShipmentTypeFilter = useCallback(() => {
    setSelectedShipmentTypes(shipmentTypes);
  }, [shipmentTypes, setSelectedShipmentTypes]);

  const onCloseDorStatusFilter = useCallback(() => {
    setSelectedDorStatuses(dorStatuses);
  }, [dorStatuses, setSelectedDorStatuses]);

  const onCloseCarrierFilter = useCallback(() => {
    setSelectedCarriers(carriers);
  }, [carriers, setSelectedCarriers]);

  const onCloseRecipientCountryFilter = useCallback(() => {
    setSelectedRecipientCountries(recipientCountries);
  }, [recipientCountries, setSelectedRecipientCountries]);

  const { isDORSigningLinkEnabled: isDorStatusFilterShown } = useClaimsShopSettings();

  return (
    <WrappableRow spacing={8}>
      <FilterSelect
        title="Status"
        styleName="status"
        label={getLabel(selectedStatuses, claimStatusOptions)}
        options={buildStatusOptions(claimStatusOptions, statusCounts)}
        selectedValues={selectedStatuses}
        onApply={onApplyButtonClick}
        onChange={setSelectedStatuses}
        onClose={onCloseStatusFilter}
        ariaLabel="status-filter-button"
        isSelectButtonDisabled={isLoading}
      />
      <FilterSelect
        title="Type"
        label={getLabel(selectedTypes, claimTypeOptions)}
        options={claimTypeOptions}
        selectedValues={selectedTypes}
        onApply={onApplyButtonClick}
        onChange={setSelectedTypes}
        onClose={onCloseTypeFilter}
        ariaLabel="type-filter-button"
        isSelectButtonDisabled={isLoading}
      />
      <FilterSelect
        title="Shipment"
        label={getLabel(selectedShipmentTypes, claimShipmentTypesOptions)}
        options={claimShipmentTypesOptions}
        selectedValues={selectedShipmentTypes}
        onApply={onApplyButtonClick}
        onChange={setSelectedShipmentTypes}
        onClose={onCloseShipmentTypeFilter}
        ariaLabel="shipment-filter-button"
        isSelectButtonDisabled={isLoading}
      />
      <FilterSelect
        title="Carrier"
        label={getLabel(selectedCarriers, claimCarrierOptions)}
        options={claimCarrierOptions}
        selectedValues={selectedCarriers}
        onApply={onApplyButtonClick}
        onChange={setSelectedCarriers}
        onClose={onCloseCarrierFilter}
        ariaLabel="carrier-filter-button"
        isSelectButtonDisabled={isLoading}
      />
      {isDorStatusFilterShown && (
        <FilterSelect
          title="DOR"
          label={getLabel(selectedDorStatuses, claimDorStatusOptions)}
          options={claimDorStatusOptions}
          selectedValues={selectedDorStatuses}
          onApply={onApplyButtonClick}
          onChange={setSelectedDorStatuses}
          onClose={onCloseDorStatusFilter}
          ariaLabel="dor-status-filter-button"
          isSelectButtonDisabled={isLoading}
        />
      )}
      <FilterSelect
        title="Recipient country"
        label={getLabel(selectedRecipientCountries, recipientCountryOptions)}
        options={recipientCountryOptions}
        selectedValues={selectedRecipientCountries}
        onApply={onApplyButtonClick}
        onChange={setSelectedRecipientCountries}
        onClose={onCloseRecipientCountryFilter}
        ariaLabel="recipient-country-filter-button"
        isSearchable
      />
      <ResetButton onClick={onClearButtonClick}>Clear filters</ResetButton>
    </WrappableRow>
  );
}

function buildStatusOptions(options, statusCounts) {
  return options.reduce((filteredOptions, option) => {
    const { label, value } = option;
    const count = statusCounts[claimStatusCountMap[value]];

    if (count) {
      filteredOptions.push({
        label,
        count,
        value,
      });
    }

    return filteredOptions;
  }, []);
}

function getLabel(selectedValues, options) {
  if (selectedValues.length === 1) {
    const selectedValue = options.find(({ value }) => value === selectedValues[0]);
    return selectedValue.buttonLabel || selectedValue.label;
  }

  if (selectedValues.length === 0 || selectedValues.length === options.length) {
    return 'All';
  }

  return 'Multiple';
}

ClaimsFilters.propTypes = {
  types: PropTypes.arrayOf(PropTypes.string),
  statuses: PropTypes.arrayOf(PropTypes.string),
  statusCounts: PropTypes.object,
  shipmentTypes: PropTypes.arrayOf(PropTypes.string),
  dorStatuses: PropTypes.arrayOf(PropTypes.string),
  recipientCountries: PropTypes.arrayOf(PropTypes.string),
  onSelectStatus: PropTypes.func,
  onSelectType: PropTypes.func,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
};

export default React.memo(ClaimsFilters);
